import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icons } from '../utils/icons';
import { authService } from '../services/authService';
import { diasEspera as calcDias } from '../utils/urgencia';

const ESTADO_LABEL = {
  pendientes: 'Pendiente',
  pendientesMaterial: 'Pend. Material',
  pendientesPresupuesto: 'Pend. Presupuesto',
  pendientesSustitucion: 'Pend. Sustitución',
  ausente: 'Ausente',
};

const ESTADO_COLOR = {
  pendientes: 'bg-gray-100 text-gray-700',
  pendientesMaterial: 'bg-amber-100 text-amber-700',
  pendientesPresupuesto: 'bg-blue-100 text-blue-700',
  pendientesSustitucion: 'bg-red-100 text-red-700',
  ausente: 'bg-purple-100 text-purple-700',
};

// Caché global para no pedir datos cada vez que se abre el modal
let cachedItems = null;
let cacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000;

// Caché aparte para el listado completo de "ordenes" (Firebase, sin
// StelOrder en vivo) — se carga en segundo plano por lotes vía cursor,
// igual que el listado de clientes en el módulo Clientes.
let cachedOrdenesGenerales = null;
let cacheOrdenesTime = 0;

async function cargarOrdenesGenerales(onBatch) {
  const now = Date.now();
  if (cachedOrdenesGenerales && now - cacheOrdenesTime < CACHE_TTL) {
    onBatch(cachedOrdenesGenerales);
    return;
  }
  const all = [];
  let cursor = null;
  do {
    const url = `/.netlify/functions/buscarOrdenesGlobal?limit=2000${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
    const r = await fetch(url, { headers: { ...authService.authHeader() } });
    const d = await r.json();
    if (!d.success) break;
    all.push(...d.ordenes);
    onBatch([...all]);
    cursor = d.nextCursor || null;
  } while (cursor);
  cachedOrdenesGenerales = all;
  cacheOrdenesTime = Date.now();
}

const SearchModal = ({ onClose, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [ordenesGenerales, setOrdenesGenerales] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const clienteTimer = useRef(null);

  // Listado completo de "ordenes" (Firebase) en segundo plano — no bloquea
  // la carga inicial de kanban+bolsa, se va ampliando el índice de búsqueda
  // a medida que llegan los lotes.
  useEffect(() => {
    cargarOrdenesGenerales(batch => {
      setOrdenesGenerales(batch.map(o => ({
        id: o.id,
        reference: o.referencia,
        customerName: o.clienteNombre,
        direccion: o.direccion,
        date: o.fechaProgramada,
        tipo: 'orden',
        estado: o.estado || 'general',
        etiqueta: ESTADO_LABEL[o.estado] || 'Orden',
      })));
    }).catch(e => console.error('SearchModal (ordenes generales) error:', e));
  }, []);

  // Fusiona kanban+bolsa (más ricas, prioritarias) con el listado general de
  // órdenes, sin duplicar una misma orden si ya viene de kanban/bolsa.
  const todosLosItems = React.useMemo(() => {
    const referenciasYaIncluidas = new Set(items.map(i => i.reference).filter(Boolean));
    const generalesSinDuplicar = ordenesGenerales.filter(o => !referenciasYaIncluidas.has(o.reference));
    return [...items, ...generalesSinDuplicar];
  }, [items, ordenesGenerales]);

  // Cargar datos (kanban + bolsa)
  useEffect(() => {
    const cargar = async () => {
      const now = Date.now();
      if (cachedItems && now - cacheTime < CACHE_TTL) {
        setItems(cachedItems);
        setLoading(false);
        return;
      }
      try {
        const [kanbanRes, bolsaRes] = await Promise.all([
          fetch(`/.netlify/functions/getKanbanWorkOrders`, { headers: { ...authService.authHeader() } }).then(r => r.json()),
          fetch(`/.netlify/functions/getBolsaTrabajo`, { headers: { ...authService.authHeader() } }).then(r => r.json()),
        ]);

        const kanbanItems = kanbanRes.columnas
          ? Object.entries(kanbanRes.columnas).flatMap(([estado, cards]) =>
              cards.map(c => ({ ...c, tipo: 'orden', estado, etiqueta: ESTADO_LABEL[estado] || estado }))
            )
          : [];

        const bolsaItems = (bolsaRes.bolsa || []).map(c => ({
          ...c,
          tipo: 'incidencia',
          estado: 'bolsa',
          etiqueta: 'Gestionada',
        }));

        const all = [...kanbanItems, ...bolsaItems];
        cachedItems = all;
        cacheTime = Date.now();
        setItems(all);
      } catch (e) {
        console.error('SearchModal error:', e);
      } finally {
        setLoading(false);
      }
    };
    cargar();
    inputRef.current?.focus();
  }, []);

  // Búsqueda de clientes (servidor) — unifica los 4 caminos que había antes:
  // nombre, teléfono fijo/móvil, email, NIF o referencia.
  useEffect(() => {
    const term = query.trim();
    if (clienteTimer.current) clearTimeout(clienteTimer.current);
    if (term.length < 2) { setClientes([]); return; }
    clienteTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`/.netlify/functions/buscarClientes?q=${encodeURIComponent(term)}&limit=8`, {
          headers: { ...authService.authHeader() },
        });
        const d = await r.json();
        setClientes(d.success ? (d.clientes || []).map(c => ({
          tipo: 'cliente',
          id: c.id,
          nombre: c.nombre || c.nombreFiscal || 'Cliente sin nombre',
          telefono: c.telefono || c.movil || '',
          ciudad: c.ciudad || '',
          referencia: c.referencia || '',
        })) : []);
      } catch { setClientes([]); }
    }, 300);
    return () => { if (clienteTimer.current) clearTimeout(clienteTimer.current); };
  }, [query]);

  // Filtrar
  const q = query.trim().toLowerCase();
  const ordenesResultado = q.length < 1 ? todosLosItems.slice(0, 20) : todosLosItems.filter(item =>
    item.reference?.toLowerCase().includes(q) ||
    item.customerName?.toLowerCase().includes(q) ||
    item.direccion?.toLowerCase().includes(q) ||
    item.etiqueta?.toLowerCase().includes(q)
  ).slice(0, 30);
  // Clientes primero cuando hay término de búsqueda — es la pregunta más común.
  const results = q.length < 1 ? ordenesResultado : [...clientes, ...ordenesResultado].slice(0, 40);

  // Reset selection when query changes
  useEffect(() => { setSelected(0); }, [query]);

  const abrirItem = useCallback((item) => {
    if (!item) return;
    if (item.tipo === 'cliente') {
      try { sessionStorage.setItem('zapata_open_cliente', String(item.id)); } catch { /* noop */ }
      onNavigate?.('v2-clientes');
      onClose();
      return;
    }
    if (item.pdfPath) window.open(item.pdfPath, '_blank', 'noopener,noreferrer');
    onClose();
  }, [onNavigate, onClose]);

  // Keyboard nav
  const handleKey = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected(s => Math.min(s + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected(s => Math.max(s - 1, 0));
    } else if (e.key === 'Enter' && results[selected]) {
      abrirItem(results[selected]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [results, selected, onClose, abrirItem]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.children[selected];
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [selected]);

  const handleItemClick = (item) => abrirItem(item);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Icons.Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Buscar cualquier orden por cliente, referencia, dirección..."
            className="flex-1 text-base text-gray-800 placeholder-gray-400 outline-none bg-transparent"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
              <Icons.X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:block text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono">ESC</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" />
            </div>
          )}

          {!loading && results.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-10">
              Sin resultados para "{query}"
            </p>
          )}

          {!loading && results.map((item, idx) => {
            const dias = calcDias(item.date);
            const isSelected = idx === selected;
            if (item.tipo === 'cliente') {
              return (
                <button
                  key={`cliente-${item.id}`}
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={() => setSelected(idx)}
                  className={`w-full text-left flex items-center gap-4 px-4 py-3 transition-colors ${
                    isSelected ? 'bg-red-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-100">
                    <Icons.Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold text-sm ${isSelected ? 'text-red-700' : 'text-gray-900'}`}>
                        {item.nombre}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">Cliente</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {[item.telefono, item.ciudad, item.referencia].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <span className="text-gray-300 text-xs flex-shrink-0">Abrir ›</span>
                </button>
              );
            }
            return (
              <button
                key={`${item.tipo}-${item.id}`}
                onClick={() => handleItemClick(item)}
                onMouseEnter={() => setSelected(idx)}
                className={`w-full text-left flex items-center gap-4 px-4 py-3 transition-colors ${
                  isSelected ? 'bg-red-50' : 'hover:bg-gray-50'
                }`}
              >
                {/* Icono tipo */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  item.tipo === 'incidencia' ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {item.tipo === 'incidencia'
                    ? <Icons.FileText className="w-4 h-4 text-green-600" />
                    : <Icons.Package className="w-4 h-4 text-gray-600" />
                  }
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold text-sm ${isSelected ? 'text-red-700' : 'text-gray-900'}`}>
                      {item.reference}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      item.tipo === 'incidencia'
                        ? 'bg-green-100 text-green-700'
                        : ESTADO_COLOR[item.estado] || 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.etiqueta}
                    </span>
                  </div>
                  {item.customerName && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{item.customerName}</p>
                  )}
                  {item.direccion && (
                    <p className="text-xs text-gray-400 truncate">{item.direccion}</p>
                  )}
                </div>

                {/* Días */}
                <div className="text-right flex-shrink-0">
                  {dias !== null && (
                    <span className={`text-xs font-medium ${
                      dias > 30 ? 'text-red-600' : dias > 7 ? 'text-amber-600' : 'text-gray-400'
                    }`}>
                      {dias === 0 ? 'Hoy' : `${dias}d`}
                    </span>
                  )}
                  {item.pdfPath && (
                    <div className="text-gray-300 mt-0.5">
                      <Icons.FileText className="w-3 h-3" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        {!loading && (
          <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              {results.length} resultado{results.length !== 1 ? 's' : ''}
              {q && ` para "${q}"`}
            </p>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span>↑↓ navegar</span>
              <span>↵ abrir PDF</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModal;
